import './FilterBar.css'

import { Input } from '../../../../Components'
import { getDateObj, getDateString } from '../../../../Services/Functions'
import { cap } from '../../../../State'
import { translations } from '../../../../translations'
import { opzioniStatoVisibili } from '../Functions'

const language = cap.language.state || 'IT'
const t = translations[language] || translations.IT

/**
 * Opzioni per il dropdown stato, basate sugli stati visibili all'utente (non sugli stati DB).
 * Gli stati agentici compaiono solo se un environment visibile ha un canale agentico.
 */
const opzioniStato = (agenticoAttivo) => [
  { name: t.allStates, id: '' },
  ...opzioniStatoVisibili(agenticoAttivo).map((s) => ({ name: s.label, id: s.code }))
]

/**
 * Barra filtri unificata per la lista conversazioni.
 * Un'unica card con header, data, dropdown isola/stato e campo ricerca sulla stessa riga.
 * @param {Object} props - Proprietà del componente
 * @param {string} props.selectedDate - Data selezionata (formato DD/MM/YYYY)
 * @param {Function} props.onDateChange - Callback cambio data
 * @param {string} props.selectedIsola - ID isola selezionata
 * @param {Function} props.onIsolaChange - Callback cambio isola
 * @param {string} props.defaultIsola - Isola di default dell'utente
 * @param {Array} props.isole - Lista delle isole disponibili [{name, id}]
 * @param {Object} props.filters - Valori filtri {traveller, aic, state, searchQuery}
 * @param {Function} props.onFilterChange - Callback cambio filtro (filterName, value)
 * @param {Function} props.onReset - Callback reset di tutti i filtri
 * @param {boolean} props.isLoading - Indicatore di caricamento dati in corso
 * @param {boolean} props.agenticoAttivo - Mostra gli stati agentici nel dropdown
 * @returns {JSX.Element} Componente barra filtri unificata
 */
export function FilterBar({
  selectedDate,
  onDateChange,
  selectedIsola,
  onIsolaChange,
  defaultIsola,
  isole,
  filters,
  onFilterChange,
  onReset,
  isLoading,
  agenticoAttivo = false
}) {
  const hasActiveFilters =
    filters.state || filters.searchQuery || (selectedIsola && selectedIsola !== defaultIsola)
  const activeFilterCount = [
    filters.state,
    filters.searchQuery,
    selectedIsola && selectedIsola !== defaultIsola ? selectedIsola : ''
  ].filter(Boolean).length

  return (
    <div className="ncard gigi-filter-bar">
      <div className="ncard__head">
        <div className="ncard__ico">
          <i className="pi pi-filter" style={{ fontSize: 16 }} />
        </div>
        <div>
          <h3 className="ncard__title">{t.filters}</h3>
          <div className="ncard__sub">{t.filtraPerDataIsolaStato}</div>
        </div>
        <div className="ncard__head-actions">
          {activeFilterCount > 0 && (
            <span className="nbadge nbadge--info">
              {activeFilterCount} {activeFilterCount === 1 ? t.activeFilterOne : t.activeFilterMany}
            </span>
          )}
          {hasActiveFilters && (
            <button
              type="button"
              className="nbtn nbtn--ghost nbtn--sm"
              onClick={onReset}
              aria-label={t.removeAllFilters}
            >
              <i className="pi pi-times" style={{ fontSize: 11 }} />
              {t.resetFilters}
            </button>
          )}
        </div>
      </div>

      <div className="ncard__body">
        <div className="gigi-filters-row">
          <div className="nfield nora-date-field gigi-date-field" style={{ minWidth: 190 }}>
            <label>{t.date}</label>
            <Input
              date
              showIcon
              id="selectedDate"
              value={getDateObj(selectedDate)}
              onChange={({ selectedDate: val }) => onDateChange(getDateString(val))}
              style={{ width: '100%' }}
            />
          </div>
          <div className="nfield" style={{ minWidth: 190 }}>
            <label>{t.island}</label>
            <Input
              dropdown
              id="isola"
              placeholder={t.allIslands}
              value={selectedIsola}
              options={[{ id: '', name: t.allIslands }, ...isole]}
              optionLabel="name"
              optionValue="id"
              onChange={(e) => onIsolaChange(e.isola)}
              showClear={!!selectedIsola && selectedIsola !== defaultIsola}
              style={{ width: '100%' }}
            />
          </div>
          <div className="nfield" style={{ minWidth: 200 }}>
            <label>{t.state}</label>
            <Input
              dropdown
              id="state"
              placeholder={t.allStates}
              value={filters.state}
              options={opzioniStato(agenticoAttivo)}
              optionLabel="name"
              optionValue="id"
              onChange={(e) => onFilterChange('state', e.state)}
              showClear={!!filters.state}
              style={{ width: '100%' }}
            />
          </div>
          <div className="nfield gigi-search-field" style={{ flex: 1, minWidth: 240 }}>
            <label>{t.search}</label>
            <div className="gigi-search-wrapper">
              <i className="pi pi-search gigi-search-icon" />
              <input
                type="text"
                id="filterSearch"
                className="gigi-search-input"
                value={filters.searchQuery || ''}
                onChange={(e) => onFilterChange('searchQuery', e.target.value)}
                placeholder={t.searchPlaceholder}
                aria-label={t.searchConversations}
              />
              {filters.searchQuery && (
                <button
                  type="button"
                  className="gigi-search-clear"
                  onClick={() => onFilterChange('searchQuery', '')}
                  aria-label={t.clearSearch}
                  tabIndex={0}
                >
                  <i className="pi pi-times" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {isLoading && <div className="gigi-section-loading-bar" />}
    </div>
  )
}
